// Test-only mirror of the backend report projection for investigation-group runs:
// DomainIntelligenceAction.buildInvestigationGroupArtifactReport() and its helpers
// (summarizeGroupReportEntities, buildEmbeddedGroupProbeIndex, buildGroupReportSummary).
//
// PURPOSE: give component tests report-shaped data derived from REAL dossiers,
// without a running backend. If the Java projection changes shape, update this
// mirror in the same change — the audit's W3.5 (Java-side JSON-schema test) is
// the long-term pin between the two.
//
// Deliberately mirrors CURRENT backend behaviour, including behaviour the audit
// wants changed (e.g. overallConfidence hardcoded to 0.0). Tests that assert the
// FIXED behaviour build on top of this and override the relevant field, so the
// mirror always states what the backend does, not what we wish it did.

import { clone } from './index'

const isFlagged = (entity) => {
    if (entity.hitScreen === true) return true
    const severity = String(entity.severity || '')
        .trim()
        .toLowerCase()
    return severity !== '' && !['healthy', 'stable', 'unknown'].includes(severity)
}

const deriveKeyFinding = (entity) => {
    const synthesisJson = entity.synthesisJson || {}
    if (synthesisJson.bluf) return synthesisJson.bluf
    if (Array.isArray(entity.actionItems) && entity.actionItems.length > 0) {
        return entity.actionItems[0]
    }
    if (entity.synthesis) return entity.synthesis.slice(0, 200)
    return null
}

// Mirrors summarizeGroupReportEntities (DomainIntelligenceAction.java:1561),
// including the 2026-07-27 F4/F5 fixes: peerEvidence and
// synthesisJson.questions_to_investigate are forwarded; probeData bulk is not.
const summarizeEntity = (entity) => {
    const summary = {}
    const copy = (field) => {
        if (entity[field] !== undefined && entity[field] !== null) {
            summary[field] = clone(entity[field])
        }
    }
    ;[
        'entityId',
        'entityType',
        'severity',
        'hitScreen',
        'unprobed',
        'hierarchy',
        'labels',
        'actionItems',
        'synthesis',
        'peerEvidence',
    ].forEach(copy)

    if (Array.isArray(entity.probeData)) {
        summary.probeCount = entity.probeData.length
        summary.investigationCount = entity.probeData.length
    }

    const keyFinding = deriveKeyFinding(entity)
    if (keyFinding) {
        summary.keyFinding = keyFinding
        summary.keyFindings = [keyFinding]
    }

    const synthesisJson = entity.synthesisJson
    if (synthesisJson && typeof synthesisJson === 'object') {
        const slim = {}
        ;[
            'bluf',
            'summary',
            'narrative',
            'analysis',
            'investigation_narrative',
            'severity',
        ].forEach((field) => {
            if (typeof synthesisJson[field] === 'string') slim[field] = synthesisJson[field]
        })
        if (Array.isArray(synthesisJson.questions_to_investigate)) {
            slim.questions_to_investigate = clone(synthesisJson.questions_to_investigate)
        }
        if (Object.keys(slim).length > 0) summary.synthesisJson = slim
    }

    return summary
}

// Mirrors buildEmbeddedGroupProbeIndex (DomainIntelligenceAction.java).
const buildProbeIndex = (dossiers) => {
    const index = []
    for (const entity of dossiers) {
        // Mirrors the Java: probeOrdinal is the 0-based position among this
        // entity's records with the same probeId (F1b — repeated passes).
        const probeIdOccurrences = {}
        for (const probe of entity.probeData || []) {
            if (!probe.probeId) continue
            const probeOrdinal = probeIdOccurrences[probe.probeId] || 0
            probeIdOccurrences[probe.probeId] = probeOrdinal + 1
            const meta = { probeId: probe.probeId, probeOrdinal }
            if (entity.entityId) meta.entityId = entity.entityId
            if (probe.naturalLanguageQuery) meta.naturalLanguageQuery = probe.naturalLanguageQuery
            if (probe.contextName) meta.contextName = probe.contextName
            if (probe.confidence != null) meta.confidence = probe.confidence
            if (probe.findingType) meta.findingType = probe.findingType
            const columns = probe.queryResult?.columns
            if (Array.isArray(columns)) meta.columnCount = columns.length
            const rows = probe.queryResult?.rows
            if (Array.isArray(rows)) meta.rowCount = rows.length
            index.push(meta)
        }
    }
    return index
}

const inferHierarchyLevels = (dossiers) => {
    const levels = []
    for (const entity of dossiers) {
        for (const level of Object.keys(entity.hierarchy || {})) {
            if (!levels.includes(level)) levels.push(level)
        }
    }
    return levels
}

/**
 * Build the getDomainIntelligenceArtifactReport response shape from dossier
 * fixtures. `overrides` merges into the top-level report for tests that need
 * to vary one field.
 */
export const buildReportFromDossiers = (dossiers, overrides = {}) => {
    const entitySummaries = dossiers.map(summarizeEntity)
    const flagged = entitySummaries.filter(isFlagged)
    const probeIndex = buildProbeIndex(dossiers)

    return {
        runPath: 'W23607/investigation-groups/2026/07/15/grp_fixture/',
        artifactType: 'investigation_group',
        timestamp: '2026-07-15T09:00:00Z',
        status: 'SUCCESS',
        statistics: {
            totalEntities: entitySummaries.length,
            flaggedEntities: flagged.length,
            totalInvestigations: entitySummaries.length,
            probeCount: probeIndex.length,
        },
        request: {
            groupId: 'grp_fixture',
            groupName: 'HO1 (fixture)',
            entityType: 'Store Number',
        },
        summary: {
            bluf: flagged[0]?.keyFinding || '',
            narrative: '',
            // Current backend behaviour (F2): hardcoded, not a real score.
            overallConfidence: 0.0,
            totalInvestigations: entitySummaries.length,
            highConfidenceCount: flagged.length,
            severityAssessment: flagged.map((entity) => ({
                indicator: entity.entityType || 'Entity',
                entityId: entity.entityId,
                level: entity.severity || 'Flagged',
                detail: entity.keyFinding || '',
            })),
        },
        investigations: [],
        entitySummaries,
        hierarchySummaries: [],
        hierarchyLevels: inferHierarchyLevels(dossiers),
        contextMetadata: {},
        probeIndex,
        entitySummariesSlimmed: true,
        probeIndexSlimmed: true,
        ...overrides,
    }
}
